import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Copy } from 'core-system/Icons/Actions'
import { SwitchToggleV2 } from 'core-system/SwitchToggle'
import Text from 'core-system/Text'
import TextField from 'core-system/TextField'
import palette from 'core-system/Themes/palette'
import RandomUtils from 'shared/RandomUtils'
import {
  ConnectionMethodContent,
  ConnectionSettings,
  connectionIntro,
  connectionMethods,
  fleetUsageAddress,
  isEmail,
} from './reportedUsageConnections'
import { merchantLabel } from './reportedUsageUpload'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.25rem;
`

const SavedNote = styled(Text)`
  margin-top: 0.35rem;
`

const Card = styled.section`
  padding: 1.25rem 1.5rem 1.5rem;
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  background: ${palette.white};
`

const MethodHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
`

const MethodCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`

const MethodBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${palette.grey.grey200};
`

const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0;
  padding-left: 1.25rem;
`

const AddressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const AddressChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 36rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: ${palette.white};
  border: 1px solid ${palette.secondary.purple200};
`

const AddressValue = styled.code`
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${palette.text.primary};
  overflow-wrap: anywhere;
  user-select: all;
`

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${palette.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${palette.primary.pink800};
    background: ${palette.primary.pink100};
  }

  &:focus-visible {
    outline: 2px solid ${palette.secondary.purple400};
    outline-offset: 2px;
  }
`

const InboxField = styled.div`
  max-width: 28rem;
`

const FleetAddress = ({
  label,
  address,
}: {
  label: string
  address: string
}) => {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current)
    },
    []
  )

  const copy = () => {
    RandomUtils.copyToClipboard(address)
    setCopied(true)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <AddressBlock>
      <Text variant='action4' color={palette.text.secondary}>
        {label}
      </Text>
      <AddressChip>
        <AddressValue>{address}</AddressValue>
        <CopyButton type='button' aria-label='Copy Fleet address' onClick={copy}>
          <Copy width={18} height={18} />
        </CopyButton>
      </AddressChip>
      <Text variant='caption' color={palette.text.secondary} aria-live='polite'>
        {copied
          ? 'Copied'
          : 'Only this program accepts reports sent to this address.'}
      </Text>
    </AddressBlock>
  )
}

const MethodCard = ({
  method,
  enabled,
  onToggle,
  address,
  inbox,
}: {
  method: ConnectionMethodContent
  enabled: boolean
  onToggle: () => void
  address: string
  inbox?: React.ReactNode
}) => (
  <Card>
    <MethodHeader>
      <MethodCopy>
        <Text variant='h4' fontWeight={600}>
          {method.title}
        </Text>
        <Text variant='body2' color={palette.text.secondary}>
          {method.summary}
        </Text>
      </MethodCopy>
      <div data-testid={`connection-toggle-${method.id}`}>
        <SwitchToggleV2 active={enabled} onClick={onToggle} size='large' />
      </div>
    </MethodHeader>
    {enabled ? (
      <MethodBody>
        <Text variant='body2' color={palette.text.secondary}>
          {method.body}
        </Text>
        <FleetAddress label={method.addressLabel} address={address} />
        {inbox}
        <Steps>
          {method.steps.map((step) => (
            <li key={step}>
              <Text variant='body2' color={palette.text.primary}>
                {step}
              </Text>
            </li>
          ))}
        </Steps>
      </MethodBody>
    ) : null}
  </Card>
)

const MerchantConnectionPanel = ({
  launchId,
  merchantId,
  settings,
  onChange,
}: {
  launchId: string
  merchantId: string
  settings: ConnectionSettings
  onChange: (next: ConnectionSettings) => void
}) => {
  const methods = connectionMethods(merchantId)
  const address = fleetUsageAddress(launchId, merchantId)
  const label = merchantLabel(merchantId)
  const trimmedInbox = settings.forwardAddress.trim()
  const inboxInvalid = trimmedInbox.length > 0 && !isEmail(trimmedInbox)
  const fleet = methods[0]
  const forward = methods[1]

  return (
    <div>
      <TitleBlock>
        <Text variant='h2' fontWeight={600}>
          {label}
        </Text>
        <Text variant='body1' color={palette.text.secondary}>
          {connectionIntro(merchantId)}
        </Text>
        <SavedNote variant='caption' color={palette.text.secondary}>
          Saved in this browser.
        </SavedNote>
      </TitleBlock>
      <Stack>
        <MethodCard
          method={fleet}
          enabled={settings.fleetEmailEnabled}
          onToggle={() =>
            onChange({
              ...settings,
              fleetEmailEnabled: !settings.fleetEmailEnabled,
            })
          }
          address={address}
        />
        <MethodCard
          method={forward}
          enabled={settings.forwardEnabled}
          onToggle={() =>
            onChange({
              ...settings,
              forwardEnabled: !settings.forwardEnabled,
            })
          }
          address={address}
          inbox={
            <InboxField>
              <TextField
                label={`Inbox that receives ${label} reports`}
                value={settings.forwardAddress}
                placeholder='reports@company.com'
                invalid={inboxInvalid}
                helpText={
                  inboxInvalid ? 'Enter a valid email address.' : undefined
                }
                onChange={(event) =>
                  onChange({
                    ...settings,
                    forwardAddress: event.target.value,
                  })
                }
              />
            </InboxField>
          }
        />
      </Stack>
    </div>
  )
}

export default React.memo(MerchantConnectionPanel)
